"use strict";
/**
 * Permission Gate Component
 *
 * Blocks app access until location permission is granted.
 * Camera is preferred for FaceTec but optional; QR fallback will be offered if unavailable.
 *
 * Features:
 * - Checks camera + location permissions on mount
 * - Shows blocking modal if location not granted (camera optional)
 * - Provides "Grant Permissions" button to trigger browser prompts
 * - Shows manual instructions if user denies permissions
 * - Allows app access when location is granted; FaceTec will fall back to QR if camera blocked
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PermissionGate = void 0;
const react_1 = __importStar(require("react"));
const react_router_dom_1 = require("react-router-dom");
const react_redux_1 = require("react-redux");
const permissionHelper_1 = require("../helpers/permissionHelper");
const doshx_controls_web_1 = require("doshx_controls_web");
const react_fontawesome_1 = require("@fortawesome/react-fontawesome");
const pro_solid_svg_icons_1 = require("@fortawesome/pro-solid-svg-icons");
const access_auth_thunk_1 = require("../reducers/slices/user/thunks/access_auth_thunk");
const constants_1 = require("../constants/constants");
const openid_sign_in_thunk_1 = require("../reducers/slices/user/thunks/openid_sign_in_thunk");
const PermissionGate = () => {
    var _a, _b, _c, _d;
    const dispatch = (0, react_redux_1.useDispatch)();
    const { accessAuth, token } = (0, react_redux_1.useSelector)((state) => state.userState);
    const [searchParams] = (0, react_router_dom_1.useSearchParams)();
    const code = searchParams.get('code');
    const [permissionState, setPermissionState] = (0, react_1.useState)({
        state: 'checking',
        camera: null,
        location: null,
        showManualInstructions: false,
    });
    // If there's a code parameter, wait for accessAuth to be fetched before proceeding
    // This prevents blocking mobile users before we know their view_type
    const [waitingForAccessAuth, setWaitingForAccessAuth] = (0, react_1.useState)(!!code);
    const [fetchedCode, setFetchedCode] = (0, react_1.useState)(null);
    // Fetch accessAuth when code parameter is present
    (0, react_1.useEffect)(() => {
        if (code && code !== fetchedCode && !token) {
            setFetchedCode(code);
            dispatch((0, openid_sign_in_thunk_1.openid_sign_in_thunk)({ code }));
        }
        else if (code && code !== fetchedCode && token) {
            setFetchedCode(code);
            dispatch((0, access_auth_thunk_1.get_access_details_thunk)({ data: { code }, token }));
        }
    }, [code, token, fetchedCode, dispatch]);
    (0, react_1.useEffect)(() => {
        // If we have a code and accessAuth is now populated, stop waiting
        if (code && accessAuth) {
            setWaitingForAccessAuth(false);
        }
        // If there's no code, don't wait
        if (!code) {
            setWaitingForAccessAuth(false);
        }
    }, [code, accessAuth]);
    // Check permissions on component mount (must be before any conditional returns)
    (0, react_1.useEffect)(() => {
        if (!waitingForAccessAuth && (accessAuth === null || accessAuth === void 0 ? void 0 : accessAuth.view_type) !== constants_1.VIEW_TYPE.MOBILE) {
            checkPermissions();
        }
    }, [waitingForAccessAuth, accessAuth]);
    // Show loading while waiting for accessAuth to be fetched
    if (waitingForAccessAuth) {
        return (react_1.default.createElement("div", { className: "fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" },
            react_1.default.createElement("div", { className: "bg-white rounded-lg shadow-2xl p-8 text-center" },
                react_1.default.createElement(doshx_controls_web_1.CircularLoaderControl, { size: "medium" }),
                react_1.default.createElement("p", { className: "mt-4 text-gray-600" }, "Verifying access..."))));
    }
    // MOBILE BYPASS: If user authenticated via mobile app, bypass permission checks
    // Mobile users are already authenticated via Sky Lifestyle app before viewing documents
    if ((accessAuth === null || accessAuth === void 0 ? void 0 : accessAuth.view_type) === constants_1.VIEW_TYPE.MOBILE) {
        console.log('📱 PermissionGate - Mobile bypass active');
        return react_1.default.createElement(react_router_dom_1.Outlet, null);
    }
    const checkPermissions = () => __awaiter(void 0, void 0, void 0, function* () {
        setPermissionState(prev => (Object.assign(Object.assign({}, prev), { state: 'checking' })));
        const result = yield (0, permissionHelper_1.checkAllRequiredPermissions)();
        if (result.allGranted) {
            setPermissionState({
                state: 'granted',
                camera: result.camera,
                location: result.location,
                showManualInstructions: false,
            });
        }
        else {
            setPermissionState({
                state: 'denied',
                camera: result.camera,
                location: result.location,
                showManualInstructions: false,
            });
        }
    });
    const handleRequestPermissions = () => __awaiter(void 0, void 0, void 0, function* () {
        setPermissionState(prev => (Object.assign(Object.assign({}, prev), { state: 'requesting' })));
        const result = yield (0, permissionHelper_1.requestAllRequiredPermissions)();
        if (result.allGranted) {
            setPermissionState({
                state: 'granted',
                camera: result.camera,
                location: result.location,
                showManualInstructions: false,
            });
        }
        else {
            setPermissionState({
                state: 'denied',
                camera: result.camera,
                location: result.location,
                failureReason: result.failureReason,
                showManualInstructions: true,
            });
        }
    });
    const handleRetry = () => {
        setPermissionState(prev => (Object.assign(Object.assign({}, prev), { showManualInstructions: false })));
        checkPermissions();
    };
    // If required permissions are granted, render the protected routes
    if (permissionState.state === 'granted') {
        return react_1.default.createElement(react_router_dom_1.Outlet, null);
    }
    // Otherwise, show blocking modal
    return (react_1.default.createElement("div", { className: "fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 sm:p-0" },
        react_1.default.createElement("div", { className: "bg-white rounded-lg shadow-2xl w-full max-w-2xl mx-auto p-4 sm:p-6 md:p-8 max-h-screen overflow-y-auto" },
            react_1.default.createElement("div", { className: "text-center mb-4 sm:mb-6" },
                react_1.default.createElement("div", { className: "text-4xl sm:text-5xl md:text-6xl mb-3 sm:mb-4" }, "\uD83D\uDD12"),
                react_1.default.createElement("h1", { className: "text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2" }, "Permissions Required"),
                react_1.default.createElement("p", { className: "text-sm sm:text-base md:text-lg text-gray-600" }, "SkySign requires location access. Camera access improves Facial Recognition, but is optional (QR fallback available).")),
            permissionState.state === 'checking' && (react_1.default.createElement("div", { className: "text-center py-6 sm:py-8" },
                react_1.default.createElement(doshx_controls_web_1.CircularLoaderControl, { size: "medium" }),
                react_1.default.createElement("p", { className: "mt-4 text-sm sm:text-base text-gray-600" }, "Checking permissions..."))),
            permissionState.state === 'requesting' && (react_1.default.createElement("div", { className: "text-center py-6 sm:py-8" },
                react_1.default.createElement(doshx_controls_web_1.CircularLoaderControl, { size: "medium" }),
                react_1.default.createElement("p", { className: "mt-4 text-sm sm:text-base text-gray-600" }, "Please allow camera and location access in the browser prompts..."))),
            permissionState.state === 'denied' && (react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement("div", { className: "mb-4 sm:mb-6 space-y-2 sm:space-y-3" },
                    react_1.default.createElement(PermissionStatusItem, { icon: pro_solid_svg_icons_1.faCamera, name: "Camera", status: ((_a = permissionState.camera) === null || _a === void 0 ? void 0 : _a.status) || 'prompt', granted: ((_b = permissionState.camera) === null || _b === void 0 ? void 0 : _b.granted) || false }),
                    react_1.default.createElement(PermissionStatusItem, { icon: pro_solid_svg_icons_1.faLocationDot, name: "Location", status: ((_c = permissionState.location) === null || _c === void 0 ? void 0 : _c.status) || 'prompt', granted: ((_d = permissionState.location) === null || _d === void 0 ? void 0 : _d.granted) || false })),
                react_1.default.createElement("div", { className: "mb-4 sm:mb-6 bg-purple bg-opacity-10 border border-purple border-opacity-30 rounded-lg p-3 sm:p-4" },
                    react_1.default.createElement("h3", { className: "font-semibold text-primary mb-2 text-sm sm:text-base" }, "Why these permissions are required:"),
                    react_1.default.createElement("ul", { className: "space-y-2 text-xs sm:text-sm text-gray-700" },
                        react_1.default.createElement("li", { className: "flex items-start" },
                            react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: pro_solid_svg_icons_1.faCamera, className: "mr-2 text-base sm:text-lg text-purple mt-0.5" }),
                            react_1.default.createElement("span", null,
                                react_1.default.createElement("strong", null, "Camera (optional):"),
                                " Used for Facial Recognition when signing documents. If blocked or unavailable, use the mobile app QR flow.")),
                        react_1.default.createElement("li", { className: "flex items-start" },
                            react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: pro_solid_svg_icons_1.faLocationDot, className: "mr-2 text-base sm:text-lg text-purple mt-0.5" }),
                            react_1.default.createElement("span", null,
                                react_1.default.createElement("strong", null, "Location:"),
                                " Required to verify document signing location for legal compliance")))),
                permissionState.failureReason && (react_1.default.createElement("div", { className: "mb-4 sm:mb-6 bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4" },
                    react_1.default.createElement("p", { className: "text-xs sm:text-sm text-red-800" },
                        react_1.default.createElement("strong", null, "Error:"),
                        " ",
                        permissionState.failureReason))),
                permissionState.showManualInstructions && (react_1.default.createElement("div", { className: "mb-4 sm:mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4" },
                    react_1.default.createElement("h3", { className: "font-semibold text-yellow-900 mb-3 text-sm sm:text-base" }, "\uD83D\uDCDD Enable permissions manually:"),
                    react_1.default.createElement("ol", { className: "space-y-2 text-xs sm:text-sm text-yellow-800" }, (0, permissionHelper_1.getBrowserPermissionInstructions)().map((instruction, index) => (react_1.default.createElement("li", { key: index, className: "pl-3 sm:pl-4" }, instruction)))))),
                react_1.default.createElement("div", { className: "flex gap-2 sm:gap-3" }, !permissionState.showManualInstructions ? (react_1.default.createElement("button", { onClick: handleRequestPermissions, className: "flex-1 bg-orange hover:bg-tertiary active:bg-tertiary text-white font-semibold py-3 sm:py-3.5 px-4 sm:px-6 rounded-lg transition-colors text-sm sm:text-base touch-manipulation" }, "Grant Permissions")) : (react_1.default.createElement("button", { onClick: handleRetry, className: "flex-1 bg-orange hover:bg-tertiary active:bg-tertiary text-white font-semibold py-3 sm:py-3.5 px-4 sm:px-6 rounded-lg transition-colors text-sm sm:text-base touch-manipulation" }, "Retry Permission Check"))),
                react_1.default.createElement("p", { className: "mt-3 sm:mt-4 text-center text-xs sm:text-sm text-gray-500" }, "Location is required to continue. Camera is optional; if unavailable, a QR option will be shown during Facial Recognition."))))));
};
exports.PermissionGate = PermissionGate;
const PermissionStatusItem = ({ icon, name, status, granted, }) => {
    const getStatusColour = () => {
        if (granted)
            return 'text-green-600';
        if (status === 'denied')
            return 'text-red-600';
        return 'text-yellow-600';
    };
    const getStatusText = () => {
        if (granted)
            return 'Granted ✓';
        if (status === 'denied')
            return 'Denied ✗';
        if (status === 'unsupported')
            return 'Not supported';
        return 'Not granted';
    };
    return (react_1.default.createElement("div", { className: "flex items-center justify-between p-2.5 sm:p-3 bg-gray-50 rounded-lg" },
        react_1.default.createElement("div", { className: "flex items-center" },
            react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: icon, className: "text-xl sm:text-2xl mr-2 sm:mr-3 text-gray-700" }),
            react_1.default.createElement("span", { className: "font-medium text-gray-900 text-sm sm:text-base" }, name)),
        react_1.default.createElement("span", { className: `font-semibold text-xs sm:text-sm ${getStatusColour()}` }, getStatusText())));
};
